import React from 'react';
import { SideNavigation, SideNavigationProps } from '@amzn/awsui-components-react';
import Popover from '@amzn/awsui-components-react/polaris/popover';
import Box from '@amzn/awsui-components-react/polaris/box';
import Link from '@amzn/awsui-components-react/polaris/link';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import { useLocalizedText } from '../hooks/localizedText';
import { useLocation, useSearchParams } from 'react-router-dom';
import { featureIsEnabled, useFeatures } from '../hooks/features';
import { externalLinks } from '../data/externalLinks';
import { publishKatalMetric } from './katalAnalytics';
import { useSpaWrapper } from './SpaLink';
import { useTranslationFeedbackHref } from '../hooks/translationFeedback';
import {
  ADMIN_EVENT_PAGE_ACCESS,
  NON_PROFIT_SEARCH,
  SUGGEST_TRANSLATION_FIX_NAV_ITEM_ID,
} from '../utils/constants';

// Auto-removes the "New" feature prompt after this date with no code change required.
// Cloudscape's announcing-new-features pattern recommends a limited window for "New" indicators;
// this gives users a longer-than-default post-launch window to discover the redesign.
const WHATS_NEW_EXPIRY = new Date('2026-11-15');

/**
 * "New" feature prompt shown next to the Search events side-nav item, following Cloudscape's
 * announcing-new-features pattern (https://cloudscape.design/patterns/general/announcing-new-features/):
 * a "New" label in the side navigation paired with a user-invoked popover that summarizes the
 * change and links to more detail. Rendered in the item's `info` slot. The click handlers stop
 * propagation so opening/dismissing the popover never triggers the nav item's navigation.
 */
function WhatsNewFeaturePrompt() {
  const { t } = useLocalizedText(['translation']);
  return (
    <span
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <Popover
        header={t('whatsNew.title')}
        position="right"
        size="medium"
        triggerType="text"
        dismissButton
        dismissAriaLabel={t('whatsNew.dismissAriaLabel')}
        content={
          <SpaceBetween size="s">
            <span>{t('whatsNew.body')}</span>
            <Link href={externalLinks.support.whatsNewFeedback} external>
              {t('whatsNew.feedbackLink')}
            </Link>
          </SpaceBetween>
        }
      >
        <Box color="text-status-info" fontWeight="bold" display="inline">
          {t('whatsNew.triggerLabel')}
        </Box>
      </Popover>
    </span>
  );
}

// Cloudscape SideNavigation doesn't support data-aci-analytics-name attributes on individual
// items, so we fire Katal metrics manually in onFollow. The metric names map to the same
// WebsiteMetric event format that Sparklytics expects for the analytics dashboard.
const NAV_METRICS: Record<string, string> = {
  '/': 'nav_search',
  '/create-event': 'nav_createEvent',
  '/my-activity': 'nav_myActivity',
  '/coordinator-activity': 'nav_coordinatorActivity',
  '/ambassadors': 'nav_communityAmbassadorDirectory',
  // Both entries report the same name: only one of the two non-profit links shows at a time, and the
  // measure is how often anyone goes looking for a non-profit, which swapping the destination should
  // not reset.
  '/nonprofits': 'nav_searchNonprofits',
  [externalLinks.sideNav.searchNonprofits]: 'nav_searchNonprofits',
  [externalLinks.sideNav.acivDashboard]: 'nav_acivDashboard',
  [externalLinks.sideNav.planEvent]: 'nav_planningPlaybooks',
  [externalLinks.sideNav.fundingSupport]: 'nav_fundingSupport',
  [externalLinks.sideNav.userGuides]: 'nav_userGuides',
  [externalLinks.support.faqs]: 'nav_faqs',
  [externalLinks.sideNav.vcWiki]: 'nav_vcWiki',
  [externalLinks.sideNav.aciWiki]: 'nav_aciWiki',
  [externalLinks.sideNav.newsletter]: 'nav_newsletter',
};

export interface SiteNavigationProps {
  /**
   * Optional navigation guard set by the current page (via outlet context in App.tsx).
   * Called before SPA navigation — returns true to allow, false to block.
   * Pages with unsaved changes (attendance sheet, create/edit event wizard) set this
   * so a side-nav click shows their confirmation modal instead of silently discarding work
   * (Cloudscape unsaved-changes pattern). Mirrors the guard in SiteBreadcrumbs.
   */
  navigationGuard: ((href: string) => boolean) | null;
}

export function SiteNavigation({ navigationGuard }: SiteNavigationProps) {
  const { t } = useLocalizedText(['translation']);
  const spaWrapper = useSpaWrapper();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const translationFeedbackHref = useTranslationFeedbackHref();

  // Build Resources section items
  const resourceItems: SideNavigationProps.Item[] = [
    {
      type: 'link',
      text: t('sideNav.communityAmbassadorProgram'),
      href: '/ambassadors',
    },
    // The in-product search replaces the link out to Benevity's own, rather than joining it, so a
    // coordinator is never offered two places to search for the same non-profit.
    featureIsEnabled(NON_PROFIT_SEARCH, features, searchParams)
      ? {
          type: 'link',
          text: t('sideNav.searchVettedNonprofits'),
          href: '/nonprofits',
        }
      : {
          type: 'link',
          text: t('sideNav.searchNonprofits'),
          href: externalLinks.sideNav.searchNonprofits,
          external: true,
        },
  ];

  // ACIV Dashboard — gated by AdminEventPageAccess feature flag
  if (featureIsEnabled(ADMIN_EVENT_PAGE_ACCESS, features, searchParams)) {
    resourceItems.push({
      type: 'link',
      text: t('sideNav.acivDashboard'),
      href: externalLinks.sideNav.acivDashboard,
      external: true,
    });
  }

  // Build footer items
  const footerItems: SideNavigationProps.Item[] = [
    { type: 'divider' },
    {
      type: 'link',
      text: t('sideNav.aciWiki'),
      href: externalLinks.sideNav.aciWiki,
      external: true,
    },
    {
      type: 'link',
      text: t('sideNav.vcWiki'),
      href: externalLinks.sideNav.vcWiki,
      external: true,
    },
    {
      type: 'link',
      text: t('sideNav.newsletter'),
      href: externalLinks.sideNav.newsletter,
      external: true,
    },
  ];

  if (translationFeedbackHref) {
    footerItems.push({
      type: 'link',
      text: t('translationFeedback.linkLabel'),
      href: translationFeedbackHref,
      external: true,
    });
  }

  const showWhatsNew = new Date() < WHATS_NEW_EXPIRY;

  const sideNavItems: SideNavigationProps.Item[] = [
    // Primary navigation links
    {
      type: 'link',
      text: t('sideNav.searchEvents'),
      href: '/',
      info: showWhatsNew ? <WhatsNewFeaturePrompt /> : undefined,
    },
    { type: 'link', text: t('sideNav.createEvent'), href: '/create-event' },
    { type: 'link', text: t('sideNav.myVolunteerActivity'), href: '/my-activity' },
    { type: 'link', text: t('sideNav.myCoordinatorActivity'), href: '/coordinator-activity' },

    { type: 'divider' },

    // Resources section-group (non-collapsible)
    {
      type: 'section-group',
      title: t('sideNav.resources'),
      items: [
        // Get started (collapsed by default)
        {
          type: 'expandable-link-group',
          text: t('sideNav.getStarted'),
          href: '#',
          defaultExpanded: false,
          items: [
            {
              type: 'link',
              text: t('sideNav.planEvent'),
              href: externalLinks.sideNav.planEvent,
              external: true,
            },
            {
              type: 'link',
              text: t('sideNav.fundingSupport'),
              href: externalLinks.sideNav.fundingSupport,
              external: true,
            },
            {
              type: 'link',
              text: t('sideNav.userGuides'),
              href: externalLinks.sideNav.userGuides,
              external: true,
            },
            {
              type: 'link',
              text: t('sideNav.faqs'),
              href: externalLinks.support.faqs,
              external: true,
            },
          ],
        },
        ...(resourceItems as SideNavigationProps.Link[]),
      ],
    },

    // Footer links
    ...footerItems,
  ];

  return (
    // Wrapper carries a stable data-testid: Cloudscape SideNavigation renders items declaratively
    // and exposes no per-element testid hook, so integration tests scope to this container instead
    // of fragile awsui_* class names (see RedVelvetReactWebsiteTests AGENTS.md selector rule).
    <div data-testid="site-navigation">
      <SideNavigation
        activeHref={location.pathname}
        header={{ text: t('sideNav.menu'), href: '#' }}
        onFollow={(event) => {
          // Prevent navigation on non-clickable section headers (href="#")
          if (event.detail.href === '#') {
            event.preventDefault();
            return;
          }
          // NAV_METRICS is keyed by a fixed href, and each language is meant to report through its
          // own link, so this one cannot be keyed there. Match it by identity instead.
          const metricName =
            NAV_METRICS[event.detail.href] ??
            (event.detail.href === translationFeedbackHref
              ? 'nav_' + SUGGEST_TRANSLATION_FIX_NAV_ITEM_ID
              : undefined);
          if (!event.detail.external) {
            // Internal links: prevent full page reload and use React Router for SPA navigation
            event.preventDefault();
            // Cloudscape unsaved-changes pattern: consult the current page's guard before
            // navigating. If it returns false, the page shows its own confirmation modal and
            // we abort (no metric, no navigation) — same behavior as SiteBreadcrumbs.
            if (navigationGuard && !navigationGuard(event.detail.href)) return;
            if (metricName) publishKatalMetric('Click', metricName);
            spaWrapper({ url: event.detail.href })(event);
          } else {
            if (metricName) publishKatalMetric('Click', metricName);
            // External links open naturally in new tab via Cloudscape's external prop
          }
        }}
        items={sideNavItems}
      />
    </div>
  );
}
